import React, { useMemo } from 'react';
import CSSTransition from 'react-transition-group/CSSTransition';

import { Trans, useTranslation } from '@lyearn/i18n';
import { ArrowIosBack } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Stack, Tooltip, Typography } from '@lyearn/ui';
import DocumentTitle from '@/components/DocumentTitle';
import { HeaderState } from '@/components/EditorViewer/hooks/types';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import BookmarkAction from '@/modules/content/components/ContentCard/BaseCard/Bookmark/index.desktop';
import SelectLanguage from '@/modules/corporate/components/DetailPageComponents/DetailHero/components/Languages';
import { ILearningContent } from '@/modules/corporate/components/LearningContent/types';
import SettingsLazy from '@/modules/corporate/components/Settings/index.lazy';
import { DialogsActions } from '@/modules/corporate/components/Settings/types';
import WorkspaceHeadline from '@/modules/corporate/components/WorkspaceHeadline';
import { useArticle } from '@/modules/corporate/modules/article/articleContext';
import useSharedArticleUpdate from '@/modules/corporate/modules/article/hooks/useSharedArticleUpdate';
import {
  headerAnimation,
  headerContentTransition,
} from '@/modules/corporate/modules/course/views/CourseViewer/components/MainContent/Header/styles';
import { getContentUrl } from '@/modules/corporate/modules/path/utils';
import JourneyContentHeader from '@/modules/journey/components/JourneyContentHeader';
import getSubHeaderStatus from '@/modules/lyearn-connect/views/LyearnConnectDetails/utils/getSubHeaderStatus';
import { LanguageConfig, LanguageEnum, ResourceStatusEnum, ResourceTypeEnum } from '@/types/schema';

import { HeaderViewProps } from './types';

const TOOLTIP_PROPS = { placement: 'bottom' as const };
const LYEARN_CONNECT_SETTINGS_CONFIG = [
  DialogsActions.VisibilitySettings,
  DialogsActions.Tags,
  DialogsActions.AssignmentRestriction,
  DialogsActions.Assign,
  DialogsActions.AttachCategories,
];
const DEFAULT_LANGUAGE = LanguageEnum.English;
const Header: React.FC<HeaderViewProps> = (props) => {
  const { headerState, goBack, articleContent, article, isPreviewing, isAdminRoute } = props;
  const { sharedByWorkspace, versionConfigs, status } = article;
  const {
    isLocalizedContent,
    languageConfigs,
    languageType,
    id,
    sourceContent,
    sourceLanguageContentId,
    localizedContents,
    defaultLanguageType,
  } = article;
  const { t } = useTranslation();
  const { journeySessionId } = useArticle();
  const { onUpdateContent } = useSharedArticleUpdate(article);

  const { view: viewUrl, manage: manageUrl } = useMemo(
    () => getContentUrl({ type: article.type, id: article.id }),
    [article.id, article.type],
  );
  const { [FeatureFlags.EnableMultiLanguageContent]: isMultiLanguageContentEnabled } =
    useFeatureFlags();
  const localizedContentsMap = useMemo(() => {
    const map = new Map();
    let localizedContens_ = localizedContents;
    if (isLocalizedContent) {
      localizedContens_ = (sourceContent as any)?.localizedContents;
    }
    localizedContens_?.forEach((localizedContent) =>
      map.set(localizedContent.id, localizedContent.status),
    );
    return map;
  }, [localizedContents]);

  const allLanguageConfigs = useMemo(() => {
    let configs: LanguageConfig[] = (sourceContent as any)?.languageConfigs ?? [];
    if (!configs.length && languageConfigs?.length) {
      configs = languageConfigs;
    }

    if (!isAdminRoute) {
      configs = configs.filter(
        (config) =>
          localizedContentsMap.get(config.contentId) &&
          localizedContentsMap.get(config.contentId) !== ResourceStatusEnum.Draft,
      );
    }
    return [
      ...configs,
      { contentId: sourceLanguageContentId ?? id, languageType: DEFAULT_LANGUAGE },
    ];
  }, [languageConfigs, sourceContent, sourceLanguageContentId, id, localizedContents]);

  const isSharedArticleAdminRoute = sharedByWorkspace && isAdminRoute;
  const isHeaderTransparent = isSharedArticleAdminRoute
    ? false
    : headerState === HeaderState.Transparent;

  return (
    <CSSTransition
      unmountOnExit
      classNames="header"
      in={headerState !== HeaderState.Hidden}
      timeout={200}>
      <Stack className={cn('absolute z-20 w-full', headerAnimation)} direction="col">
        <DocumentTitle title={articleContent.title} />
        <Stack
          alignItems="center"
          className={cn('z-20 h-56 w-full flex-shrink-0 px-20', {
            'border-b border-divider bg-bg-primary border-s-0 xs:border-b-0':
              headerState === HeaderState.Standard || isSharedArticleAdminRoute,
          })}
          data-id="header"
          justifyContent="between">
          {isPreviewing ? null : (
            <Stack
              alignItems="center"
              className="z-20 h-full"
              data-id="back-button"
              direction="row"
              gapX={12}>
              <Tooltip title={<Trans i18nKey="Article:ExitArticle" />}>
                <ArrowIosBack
                  className={cn(
                    'h-20 w-20 flex-shrink-0 cursor-pointer rtl:rotate-180',
                    headerContentTransition,
                  )}
                  fillColorToken={isHeaderTransparent ? 'icon-white' : 'icon-active'}
                  onClick={goBack}
                />
              </Tooltip>
            </Stack>
          )}
          <Stack alignItems="center" className="flex-1 ms-10" gapX={12} justifyContent="center">
            <Typography
              className={headerContentTransition}
              color={isHeaderTransparent ? 'text-text-white' : 'text-text-primary'}
              data-id="title" // safari issue - text breaks into multiple lines in some versions though not needed
              numberOfLines={1}
              variant="body-short-02">
              {articleContent.title}
            </Typography>
          </Stack>
          {allLanguageConfigs?.length <= 1 ||
          !isMultiLanguageContentEnabled ||
          isAdminRoute ? null : (
            <SelectLanguage
              id={id as string}
              languageConfigs={allLanguageConfigs}
              languageType={(languageType ?? defaultLanguageType) as LanguageEnum}
              resourceType={ResourceTypeEnum.Article}
            />
          )}
          {!isAdminRoute ? (
            <BookmarkAction
              appearance={isHeaderTransparent ? 'inverse' : 'primary'}
              bookmarkId={article.bookmark?.id}
              className={cn('me-18', headerContentTransition)}
              contentId={article.id}
              contentType={article.type}
              data-id="bookmark"
              tooltipProps={TOOLTIP_PROPS}
            />
          ) : (
            <SettingsLazy
              allUrls={{ viewUrl, manageUrl }}
              config={LYEARN_CONNECT_SETTINGS_CONFIG}
              isSharedItem={!!sharedByWorkspace}
              item={article}
              itemHeader={t('Common:ResourceTypeSM.Article')}
              onSubmit={onUpdateContent}
            />
          )}
        </Stack>
        {journeySessionId?.length ? (
          <JourneyContentHeader
            content={article as ILearningContent}
            contentType={ResourceTypeEnum.Article}
            sessionId={journeySessionId}
          />
        ) : null}
        {isSharedArticleAdminRoute ? (
          <WorkspaceHeadline
            content={t('Common:ResourceTypeSM.Article')}
            status={getSubHeaderStatus(article.status)}
            workspaceName={sharedByWorkspace.workspaceName!}
          />
        ) : null}
      </Stack>
    </CSSTransition>
  );
};

export default React.memo(Header);
